import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Minus, Plus, ShoppingBag, Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL, CONDITIONS, RARITY_STYLE, fmtDate } from "@/lib/constants";
import { useShop } from "@/context/ShopContext";
import { CartAlert } from "@/components/shop/CartAlert";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/shop/StarRating";
import { Loader } from "@/components/shop/ProtectedRoute";
import { ReleaseCountdown, WaitlistForm } from "@/components/shop/PreorderExtras";
import { ProductGallery, AuthenticityNote, isHighValue } from "@/components/shop/ProductGallery";
import { useShippingConfig, promoSummary } from "@/lib/shipping";

const HELD_MSG = "Il prodotto è stato inserito nel carrello di un altro utente";
const Reviews = ({ product, onUpdated }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${product.id}/reviews`).then((r) => setReviews(r.data));
  useEffect(() => { load(); }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (window.location.hash !== "#recensioni") return;
    const t = setTimeout(() => document.getElementById("recensioni")?.scrollIntoView(), 700);
    return () => clearTimeout(t);
  }, [product.id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/reviews`, { rating, comment });
      toast.success("Grazie per la tua recensione!");
      setComment("");
      load();
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-20 scroll-mt-32" id="recensioni" data-testid="reviews-section">
      <div className="flex flex-wrap items-end gap-6">
        <h2 className="font-display text-2xl font-extrabold sm:text-3xl">Recensioni</h2>
        <div className="flex items-center gap-2 text-stone-600">
          <span className="font-mono text-2xl font-bold text-amber-600" data-testid="reviews-average">{product.rating_count ? product.rating_avg.toFixed(1) : "—"}</span>
          <StarRating value={product.rating_avg} size={16} />
          <span className="text-sm" data-testid="reviews-count">({product.rating_count})</span>
        </div>
      </div>
      <div className="mt-8 grid gap-10 lg:grid-cols-[380px_1fr]">
        <div className="rounded-2xl border border-black/10 bg-[#FFFFFF] p-6">
          {user ? (
            <form onSubmit={submit} className="space-y-4" data-testid="review-form">
              <p className="font-display font-bold">Scrivi una recensione</p>
              <StarRating value={rating} size={24} onChange={setRating} testid="review-rating" />
              <textarea required minLength={3} value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder="Com'è il prodotto? Condizioni come descritte?" data-testid="review-comment-input"
                className="w-full rounded-xl border border-black/10 bg-[#FFFBF3] p-3 text-sm outline-none focus:border-amber-500/50" />
              <button disabled={busy} data-testid="review-submit-button" className="w-full rounded-full bg-amber-500 py-3 font-display font-bold text-black hover:bg-amber-400 disabled:opacity-60">Pubblica</button>
            </form>
          ) : (
            <p className="text-sm text-stone-600"><Link to={`/accedi?next=/prodotto/${product.id}`} className="text-amber-600 underline" data-testid="review-login-link">Accedi</Link> per lasciare una recensione.</p>
          )}
        </div>
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.length === 0 && <p className="text-stone-500">Nessuna recensione ancora. Sii il primo!</p>}
          {reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-black/[0.06] bg-[#FFF6E6] p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold">{r.user_name}</p>
                <span className="text-xs text-stone-500">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
              </div>
              <StarRating value={r.rating} size={13} />
              <p className="mt-2 text-sm text-stone-700">{r.comment}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Spec = ({ label, value }) => value ? (
  <div className="rounded-xl border border-black/[0.06] bg-[#FFF6E6] p-3">
    <p className="text-[11px] uppercase tracking-wider text-stone-500">{label}</p>
    <p className="mt-0.5 text-sm font-semibold">{value}</p>
  </div>
) : null;

export default function ProductPage() {
  const { id } = useParams();
  const shipCfg = useShippingConfig();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [missing, setMissing] = useState(false);
  const { addToCart, wishlist, toggleWishlist, cart } = useShop();
  const inMine = !!p && cart.some((i) => i.product_id === p.id);
  const held = p?.in_other_carts && !inMine;

  const load = () => api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  useEffect(() => { setQty(1); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="py-32 text-center text-stone-600" data-testid="product-not-found">Prodotto non trovato. <Link to="/catalogo" className="text-amber-600">Torna al catalogo</Link></div>;
  if (!p) return <Loader />;
  const wished = wishlist.includes(p.id);
  const cond = CONDITIONS.find((c) => c.value === p.condition)?.label;

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-10 sm:px-6 lg:px-12">
      <p className="text-sm text-stone-500"><Link to="/catalogo" className="hover:text-amber-600">Catalogo</Link> / <Link to={`/catalogo?category=${p.category}`} className="hover:text-amber-600">{CATEGORY_LABEL[p.category]}</Link></p>
      <div className="mt-6 grid gap-12 md:grid-cols-2">
        <ProductGallery p={p} />
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.2em] text-violet-500">{p.game}{p.set_name ? ` · ${p.set_name}` : ""}</p>
          <h1 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl" data-testid="product-name">{p.name}</h1>
          {p.rating_count > 0 && <div className="mt-3 flex items-center gap-2 text-sm text-stone-600"><StarRating value={p.rating_avg} />{p.rating_avg.toFixed(1)} · {p.rating_count} recensioni</div>}
          <p className="mt-6 font-mono text-4xl font-bold text-amber-600" data-testid="product-detail-price">{eur(p.price)}</p>
          {p.channel === "b2b" && <p className="mt-1 text-sm text-stone-500" data-testid="product-detail-vat">Prezzo B2B IVA esclusa · {eur(p.price * 1.22)} IVA inclusa</p>}
          {p.market_price > p.price && <p className="mt-1 text-sm text-stone-500" data-testid="product-market-compare">Prezzo di mercato <span className="line-through">{eur(p.market_price)}</span> · <span className="font-semibold text-rose-500">risparmi {eur(p.market_price - p.price)}</span></p>}
          {p.preorder ? (
            <div className="mt-3 rounded-xl border-2 border-violet-500 bg-violet-500/10 p-4 text-sm" data-testid="product-preorder-box">
              <p className="font-display text-lg text-violet-500">Preordine · uscita il {fmtDate(p.release_date)}</p>
              <p className="mt-1 text-stone-700">{p.preorder_mode === "caparra" ? `Paghi ora una caparra confirmatoria del ${p.deposit_pct}%, il saldo all'uscita.` : "Pagamento totale anticipato."}</p>
              <p className={`mt-1 font-semibold ${p.stock > 0 || inMine || p.held_for_you ? "text-emerald-700" : "text-rose-500"}`} data-testid="product-stock">
                {p.login_required ? "Accedi per vedere la disponibilità" : p.stock > 0 ? `Allocazione disponibile: ${p.stock} pezzi` : inMine ? "Già nel tuo carrello" : p.held_for_you ? "Riservato per te" : held ? HELD_MSG : "Allocazione esaurita: preordini chiusi"}
              </p>
              <ReleaseCountdown date={p.release_date} />
              {!p.login_required && p.stock <= 0 && !held && <WaitlistForm productId={p.id} />}
            </div>
          ) : !p.login_required && (
          <p className={`mt-2 text-sm ${p.stock > 0 || inMine || p.held_for_you ? "text-emerald-600" : "text-rose-500"}`} data-testid="product-stock">
            {p.stock > 0 ? (p.stock <= 2 ? `Solo ${p.stock} disponibil${p.stock === 1 ? "e" : "i"}!` : `Disponibile (${p.stock} pezzi)`) : inMine ? "Già nel tuo carrello" : p.held_for_you ? "Riservato per te" : held ? HELD_MSG : "Esaurito"}
          </p>
          )}
          <CartAlert p={p} held={held} onChange={load} />
          {isHighValue(p) && <AuthenticityNote p={p} cond={cond} />}
          <p className="mt-6 leading-relaxed text-stone-700">{p.description}</p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Spec label="Categoria" value={CATEGORY_LABEL[p.category]} />
            <Spec label="Condizione" value={cond} />
            <Spec label="Lingua" value={p.language} />
            <Spec label="Finitura" value={p.finish} />
            <Spec label="Rarità" value={p.rarity} />
            <Spec label="Espansione" value={p.set_name} />
            <Spec label="Numero" value={p.card_number} />
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <>
            <div className="flex h-12 items-center rounded-full border border-black/10">
              <button className="h-full px-4 hover:text-amber-600" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center font-mono" data-testid="qty-value">{qty}</span>
              <button className="h-full px-4 hover:text-amber-600" onClick={() => setQty(Math.max(1, Math.min(p.stock + (p.held_for_you ? 1 : 0), qty + 1)))} data-testid="qty-increase"><Plus className="h-4 w-4" /></button>
            </div>
            <button disabled={p.stock <= 0 && !p.held_for_you} onClick={async () => { await addToCart(p, qty); load(); }} data-testid="add-to-cart-button"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-amber-500 px-8 font-display font-bold text-black transition-colors hover:bg-amber-400 disabled:bg-stone-300 disabled:text-stone-500">
              <ShoppingBag className="h-4 w-4" />{p.preorder ? "Preordina" : "Aggiungi al carrello"}
            </button>
            </>
            <button onClick={() => toggleWishlist(p.id)} data-testid="product-wishlist-button" className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 hover:border-rose-400/50" aria-label="Lista desideri">
              <Heart className={`h-5 w-5 ${wished ? "fill-rose-500 text-rose-500" : ""}`} />
            </button>
          </div>
          <div className="mt-8 space-y-2 text-sm text-stone-600">
            <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-amber-600" />{promoSummary(shipCfg)} · <Link to="/spedizioni" className="underline" data-testid="product-shipping-link">Spedizioni e resi</Link></p>
            <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" />Carte spedite in sleeve + toploader</p>
          </div>
        </div>
      </div>
      <Reviews product={p} onUpdated={load} />
    </div>
  );
}
